import clsx from 'clsx';
import React from 'react';

import ProExclusiveLogo from '@components/ProExclusiveLogo';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { HeaderProps } from '@interfaces/ti50';
import {
  article_path,
  ti50_database_path,
  ti_50_list_2020_path,
  ti_50_list_2021_path,
  ti_50_list_2022_path,
  ti_50_list_2023_path,
} from '@routes';
import { AuthoredContentReactToHtmlParser } from '@utils/reactHtmlParserTransform';

import HeaderCategoryList from '../Header/HeaderCategoryList';
import Search from '../Search';

const Header = ({
  activeCategories,
  companyNamesOptions,
  isSEOBot,
  project,
  unlocked,
}: HeaderProps) => {
  const hydrated = useServerSideRendering();

  return (
    <div className="px-12">
      <div className="mx-auto w-full max-w-full sm:w-3/4">
        {project.isPro && <ProExclusiveLogo />}
        <h1 className="flex flex-col items-center text-center font-suisse-screen">
          <span className="font-suisse-works text-headline-xxs xs:text-headline-sm md:text-headline-lg">
            The Information’s
          </span>
          <span className="text-headline-sm xs:text-headline-alt-md md:text-headline-alt-lg">
            50 Most Promising Startups
          </span>
          <span className="max-w-fit border-0 border-y-[1px] border-solid border-primary-magenta text-headline-xxs leading-[170%] xs:text-headline-sm md:text-headline-lg">
            2024
          </span>
        </h1>
        <h2 className="sr-only">{project.title}</h2>
        <div className="mx-auto w-4/5">
          {unlocked || isSEOBot ? (
            <div
              className={clsx('mt-48 text-body-md xs:text-body-lg', {
                'seo-paywall-content': isSEOBot,
              })}
              id="ti50-2024-description"
              data-nosnippet
            >
              {AuthoredContentReactToHtmlParser(project.description)}
            </div>
          ) : (
            <p className="mt-48 text-body-md xs:text-body-lg" id="ti50-2024-description">
              Companies with the potential to be the most valuable businesses in their
              categories—based on current revenues, business models, and growth prospects.
            </p>
          )}
          {project.article?.publishedAt && (
            <p className="mt-32 text-body-md xs:text-body-lg">
              Read{' '}
              <a
                className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
                href={article_path(project.article.slug)}
              >
                {project.article.title}
              </a>
            </p>
          )}
          <p className="mt-32 text-body-md xs:text-body-lg">
            Get the latest update: see{' '}
            <a
              className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
              href={ti50_database_path()}
            >
              how these companies
            </a>{' '}
            have fared since making our list, amid economic uncertainty and a rise in new
            technologies.
          </p>
          <p className="mt-32 text-body-md xs:text-body-lg">
            <span>Other years: </span>
            <a
              className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
              href={ti_50_list_2023_path()}
            >
              2023
            </a>{' '}
            |{' '}
            <a
              className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
              href={ti_50_list_2022_path()}
            >
              2022
            </a>{' '}
            |{' '}
            <a
              className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
              href={ti_50_list_2021_path()}
            >
              2021
            </a>{' '}
            |{' '}
            <a
              className="font-bold underline hover:text-primary-magenta dark:hover:text-primary-pink"
              href={ti_50_list_2020_path()}
            >
              2020
            </a>
          </p>
        </div>
        <HeaderCategoryList
          activeCategories={activeCategories}
          hasHoverImage={false}
          projectSlug={project.slug}
        />
        {hydrated && unlocked && <Search companyNamesOptions={companyNamesOptions} />}
      </div>
    </div>
  );
};

export default Header;
